import React, {ReactElement, useContext, useState} from 'react';
import {errorSink} from '../ErrorSink';
import {FormContext, FormContextType} from './MDForm';
import {DeclaredComponent} from '../DynamicComponent';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import {YearPicker} from '../fields/YearPicker';
import {propsToCSS} from '../css';
import {CodeRefEnum} from '../fields/CodeRefEnum';
import {CollectorMake} from '../fields/CollectorMake';
import {Checkbox} from '../fields/Checkbox';
import {BASIC_ELEMENT_SCHEMA, BasicElementProps} from '../layout/Group';
import {merge} from 'lodash';
import {DatePicker} from '../fields/DatePicker';
import Row from "antd/lib/grid/row";
import Col from "antd/lib/grid/col";

import {ModelContext} from './ModelSource';

import {SelectAppUpdater} from '../fields/SelectAppUpdater';
import {CheckToggle} from '../fields/CheckToggle';

import {TooltipHint} from './TooltipHint';
import {ApplicationActionsContext} from "../data/ApplicationDataSource";
import {Attribute, AttributeCondition} from "./api/attribute";
import {FieldControlProps} from "./fieldControlProps";
import {FormUIConfigContext} from "./formUIContext";
import { FormValidationContext } from '../../context/FormValidationProvider';
import { AsYouType } from 'libphonenumber-js';

export type FieldProps = {
  attr: string,
  dataType: string,
  label: ReactElement|string,
  component: any,
  value: any,
  hintText: string,
  autoSave: boolean,
  resetDependentFields: string[],
  props: any,

  // it's used(being read) indirectly by MDForm
  defaultValueMapping: string,

} & BasicElementProps;

export function Field({attr: attrId, dataType, label, component, hintText, defaultValueMapping, autoSave = false, autoSaveOnBlur = false, resetDependentFields,
                        ...props}: FieldProps) {

  const [domId] = useState(() => `${attrId}:${COUNTER++}`);
  const formContext = useContext(FormContext);
  const actions = useContext(ApplicationActionsContext);
  const cfg = useContext(FormUIConfigContext);
  const formValidationContext = useContext(FormValidationContext);

  const attr: Attribute = formContext.metadata[attrId];

  if (!attr) {
    return errorSink(`attr '${attrId}' doesn't exist`, attrId);
  }

  const {touched, errors} = formContext.getStatus(attrId);

  if (!label) {
    label = attr.label;
  }
  if (attr.requiredInd === false) {
    label = <>{label} <span style={{ opacity: 0.87 }}>(Optional)</span></>
  }
  else if ( (!formContext.getValueByAttr(attrId) && !(component)) || (component && formContext.getValueByAttr(attrId) === undefined) ){
  	label = <><span style={{ color: 'red' }}>{label}</span></>
  }

  if (attrId === 'InsuredRiskAddr.StateProvCd' && formContext.getValueByAttr(attrId) === undefined){
      let attribute = formContext.metadata[attrId];
      formContext.setValueByAttr(attrId, attribute.defaultValue);
  }

  // ONEUAT-24786 - Cascade a field value down to new vehicles
  if (attrId === 'Vehicle.SalvageValueInd' && formContext.getValueByAttr(attrId) === undefined ){
      let applicationModel = useContext(ModelContext);
      let firstVehicle = applicationModel.getValue('lines[0].risks[0].vehicle')
      if (firstVehicle !== undefined && firstVehicle.salvageValueInd !== undefined){
          formContext.setValueByAttr(attrId, firstVehicle.salvageValueInd);
      }
  }

  props = {
    value: formContext.getValueByAttr(attrId),
    onChange: eventOrValue => {
      let fieldValue = universalGet(eventOrValue);
      if (attr.dataType === 'Phone' && fieldValue.length >= 5) {
        fieldValue = new AsYouType('US').input(fieldValue);
      }
      formContext.setValueByAttr(attrId, fieldValue);
      // ONEUAT-19632: Check to ensure that it is not saving after each character is entered in text box.
      if(attrId != 'Line.DisabilityInsurerName' && attrId != 'Line.HealthInsurerName') {
          if (autoSave) {
            actions.saveApplication();
          }
      }
      if(resetDependentFields){
        resetDependentFields.forEach((field)=> {
          if(formContext.getValueByAttr(field) !== undefined){
              let fieldMapping = formContext.metadata[field].mapping;
              formContext.setValue(fieldMapping,undefined);
              formContext.touch(field,false);
          }
          });
      }

      // ONEUAT-8816
	  if (attrId === 'Insured.SuffixCd'){
	      let fieldMapping = formContext.metadata['Insured.ContactSuffixCd'].mapping;
	      formContext.setValue(fieldMapping,formContext.getValueByAttr('Insured.SuffixCd'));
	      formContext.touch('Insured.ContactSuffixCd',false);
      }

    },
    onBlur: eventOrValue => {
      if(dataType === 'MoneyHundreds'){
        let value = formContext.getValueByAttr(attrId)
        if(value < 100) value = 100;
        if(value % 100 != 0) value = value - value % 100;
        formContext.setValueByAttr(attrId, value)
        actions.saveApplication();
      }
      // ONEUAT-19632: Immediately save after leaving the input text box to resolve Validation error and to rate properly
      if(attrId === 'Line.DisabilityInsurerName' || attrId === 'Line.HealthInsurerName') {
        if (autoSave) {
          actions.saveApplication();
        }
      }
    },
    ...propsToCSS(props),
    id: domId,
    ...props
  };

  if (attr.attributeRelationships) {
    for (let attrRel of attr.attributeRelationships) {
      if (attrRel.typeCd === 'ApplicableIf' && attrRel.attributeConditions) {
        if (!formContext.evalConditions(attrRel.attributeConditions)) {
          return null;
        }
      }
      // Conditions via comparisons should be available in a future upgrade...
      if (attrRel.typeCd === 'ApplicableCompare' && attrRel.attributeConditions) {
        for (let cond of attrRel.attributeConditions) {
          let o = cond.operator.toLowerCase();
          if(o == "greaterthan"){
            if(parseInt(formContext.getValueByAttr(cond.attributeId)) <= parseInt(cond.value)){
              return null;
            }
          }
          if(o == "lessthan"){
            if(parseInt(formContext.getValueByAttr(cond.attributeId)) >= parseInt(cond.value)){
              return null;
            }
          }
        }
      }
    }
  }

  // ONEUAT-25832 - Default value for vehicle garage address postal code
  if (attrId === 'VehicleGarageAddr.PostalCode' && formContext.getValueByAttr(attrId) === undefined){
    let applicationModel = useContext(ModelContext);
    let postalCode = applicationModel.getValue('insured.partyInfo[0].addresses[0].postalCode')
    formContext.setValueByAttr(attrId, postalCode);
  }

  let filedImpl;

  if (component) {
    filedImpl = (
      <DeclaredComponent declaration={component} {...props} />
    );
  } else {
    filedImpl = getComponentForField(dataType, attr, props);
  }

const hasErrors = touched && errors && errors.length;

  return (
    <Row {...cfg.row}>
      {!cfg.noLabels && <Col {...cfg.label}>
        <label htmlFor={domId}>
          {label}

          {hintText && <TooltipHint style={{marginLeft: '1em'}} hintText={hintText}/>}
        </label>
      </Col> }
      <Col {...cfg.control}>
        <div className={hasErrors && 'has-error'}>{filedImpl}</div>
        {hasErrors ? errors.map(err => <div className='fieldError' key={err}>{err}</div>) : <div className='fieldError' >&nbsp;</div>}
      </Col>
    </Row>
  )
}
/*
 * TODO: support the other attributeConditoins and combinations of them.  Currently only simple IF and OR are supported with operator EQUAL
 */


function getComponentForField(dataType: string, attr: Attribute, props: FieldControlProps<any>) {
  let value = props.value;
  if (attr.productListConstraint || attr.globalListConstraint) {
    if (dataType === 'SelectUpdate') {
      return <SelectAppUpdater attr={attr} {...props} />;
    }
    return <CodeRefEnum attr={attr} {...props} />;
  }
  let Component;
  dataType = dataType ? dataType : attr.dataType;
  Component = getComponentByDataType(dataType);

  if (dataType === 'Money') {
    return (
      <Component
        aria-label="Money amount"
        value={value !== undefined ? value : ''}
        formatter={value => {
          if(isNaN(value)){
            return '$0.00';
          }
          else{
            return  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        }}
        parser={value => value.replace(/\$\s?|(,*)/g, '')}
        precision={2}
        {...props}
      />
    )
  }
  if (dataType === 'MoneyHundreds') {
      return (
        <Component
          aria-label="Money amount"
          value={value !== undefined ? value : ''}
          formatter={value => {
            if(isNaN(value)){
              return '$0.00';
            }
            else{
              return  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            }
          }}
          parser={value => {
            return value.replace(/\$\s?|(,*)/g, '')
          }}
          precision={2}
          step={100}
          {...props}
        />
      )
    }
  if (dataType === 'DayNumber') {
    return (
      <Component
        aria-label="Day as number"
        value={value !== undefined ? value : ''}
        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        parser={value => value.replace(/\$\s?|(,*)/g, '')}
        max={31}
        min={1}
        {...props}
      />
    )
  }

  if (dataType === 'Integer') {
      return (
          <InputNumber
              aria-label="Integer number"
              value={value !== undefined ? value : ''}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              style={{ width: '100%' }}
              {...props}
          />
      )
  }

  if (dataType === 'CollectorMake') {
    return <CollectorMake attr={attr} {...props} />;
  }

  return (
    <Component
      value={value !== undefined ? value : ''}
      {...props}
    />
  )
}

function getComponentByDataType(dataType) {
  if (dataType === 'String') {
    return Input;
  } else if (dataType === 'Year') {
    return YearPicker;
  } else if (dataType === 'Boolean') {
    return Checkbox;
  } else if (dataType === 'Date') {
    return DatePicker;
  } else if (dataType === 'Integer') {
    return InputNumber;
  } else if (dataType === 'Money') {
    return InputNumber;
  } else if (dataType === 'MoneyHundreds') {
     return InputNumber;
  } else if (dataType === 'DayNumber') {
    return InputNumber;
  } else if (dataType === 'CheckToggle') {
    return CheckToggle;
  } else if (dataType === 'CollectorMake') {
      return CollectorMake;
  }

  return Input;
}

function universalGet(eventOrValue) {
  return eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;
}

Field.schema = merge({
  properties: {
    attr: {
      type: 'string',
    },
    label: {
      type: 'string',
    },
  }
}, BASIC_ELEMENT_SCHEMA);

let COUNTER = 0;